import { Alert, Button, Card, CardContent, Field, FieldHint, Input, Label } from "@breenova/ui";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api } from "@/lib/api";
import { AuthLayout } from "@/routes/auth-layout";

const MINIMUM_PASSWORD_LENGTH = 12;

/**
 * Requests a reset link.
 *
 * The confirmation is shown whatever the server said, because the server itself answers
 * identically for a known and an unknown address. Showing "no such account" here would
 * undo that on the client and turn this form into an account-existence check.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);

    try {
      await api.POST("/api/v1/auth/forgot-password", { body: { email: email.trim() } });
    } catch {
      // Even a network failure shows the same confirmation. The alternative leaks
      // nothing useful and only invites the breeder to retry against a down server.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <Alert tone="success">
          If that address has a BreeNova account, a reset link is on its way. The link is
          good for one use.
        </Alert>
        <p className="text-center text-body text-on-surface-variant">
          <Link to="/login" className="text-primary underline">
            Back to sign in
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We will email you a link.">
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-md" noValidate>
            <Field>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                inputMode="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>

            <Button type="submit" block disabled={submitting}>
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-center text-body text-on-surface-variant">
        <Link to="/login" className="text-primary underline">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}

/** Completes a reset using the token from the emailed link. */
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") ?? "";
  const email = searchParams.get("email") ?? "";

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);

    if (password !== confirmation) {
      setError("The two passwords do not match.");
      return;
    }

    if (password.length < MINIMUM_PASSWORD_LENGTH) {
      setError(`Your password needs at least ${MINIMUM_PASSWORD_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);

    const { error: failed } = await api.POST("/api/v1/auth/reset-password", {
      body: { email, token, newPassword: password },
    });

    setSubmitting(false);

    if (failed) {
      const problem = failed as { detail?: string; title?: string };
      setError(problem.detail ?? problem.title ?? "That reset link is invalid or has expired.");
      return;
    }

    // Every session was revoked server-side, so signing in again is the only way on.
    await navigate("/login", { replace: true });
  }

  if (!token || !email) {
    return (
      <AuthLayout title="Reset your password">
        <Alert tone="error">
          This link is incomplete. Request a new one from the sign-in screen.
        </Alert>
        <p className="text-center text-body text-on-surface-variant">
          <Link to="/forgot-password" className="text-primary underline">
            Request a new link
          </Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle={email}>
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-md" noValidate>
            {error ? <Alert tone="error">{error}</Alert> : null}

            <Field>
              <Label htmlFor="password">New password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MINIMUM_PASSWORD_LENGTH}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <FieldHint>At least {MINIMUM_PASSWORD_LENGTH} characters.</FieldHint>
            </Field>

            <Field>
              <Label htmlFor="confirmation">Repeat new password</Label>
              <Input
                id="confirmation"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>

            <Button type="submit" block disabled={submitting}>
              {submitting ? "Saving…" : "Set new password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="text-center text-body text-on-surface-variant">
        Changing your password signs out every other device.
      </p>
    </AuthLayout>
  );
}
